// src/Dashboard/admin/sections/CommsSection.jsx
import {
  RefreshCw,
  MessageSquare,
  Mail,
  CheckCircle2,
  Building2,
  Send,
  X,
  Search,
  Users,
  Trash2,
  Layers,
} from "lucide-react";
import { Badge } from "../components/ui";

/**
 * CommsSection (SMS + Email)
 * - FIXED: currentScopeParams can be missing or not a function (no runtime crash)
 * - Modern send UX:
 *    Modes:
 *     1) Filter (current scope)
 *     2) User IDs (manual paste)
 *     3) Individuals (search + select recipients)
 *     4) Audience (selected groups from AudiencePicker)  <-- wiring later
 *
 * Notes:
 * - This component is UI + orchestration only; data fetch happens in AdminDashboard.
 * - Backwards compatible with your existing props (it won’t crash if new props aren’t wired yet).
 */
export default function CommsSection({
  // page
  activeSection, // "sms" | "email"

  // meta
  scopeChips = [],
  providerStatus = { email: null, sms: null },
  commsLoading = false,
  commsError = "",
  loadCommsMeta,

  // send form (existing)
  commsMode = "filter",
  setCommsMode,

  commsUserIdsText = "",
  setCommsUserIdsText,

  commsBatchLabel = "",
  setCommsBatchLabel,

  commsSubject = "",
  setCommsSubject,

  commsBody = "",
  setCommsBody,

  currentScopeParams, // fn OR object
  onSendComms, // fn(channel, payload)
  commsLastResult,
  setCommsLastResult,

  // NEW (optional): Individuals search + selection
  recipientQuery = "",
  setRecipientQuery,
  recipientResults = [], // [{id, full_name/fullName, email, phone}]
  selectedRecipients = [], // same shape as results OR [{id, ...}]
  onAddRecipient, // (person) => void
  onRemoveRecipient, // (idOrPerson) => void
  maxRecipients = 500, // UI cap, real cap is server side

  // NEW (optional): Audience picker selection summary
  audienceSelection, // any object; we will pass to payload as-is
  onOpenAudiencePicker, // () => void
  onClearAudience, // () => void

  // batches
  batchesLoading = false,
  batches = [],
  batchesQ = "",
  setBatchesQ,
  batchesOffset = 0,
  setBatchesOffset,
  loadCommsBatches, // fn(channel)

  // logs
  logsLoading = false,
  logs = [],
  logsQ = "",
  setLogsQ,
  logsStatus = "",
  setLogsStatus,
  logsBatchId = "",
  setLogsBatchId,
  logsOffset = 0,
  setLogsOffset,
  loadCommsLogs, // fn(channel)
}) {
  const channel = activeSection === "sms" ? "sms" : "email";
  const isSms = channel === "sms";

  const providerOk = isSms ? !!providerStatus?.sms?.ok : !!providerStatus?.email?.ok;

  // ✅ FIX: never assume it's a function
  const scopeParams =
    typeof currentScopeParams === "function"
      ? currentScopeParams()
      : (currentScopeParams || {});

  // Safe setters (no crashes if parent hasn’t wired yet)
  const setMode = setCommsMode || (() => {});
  const setUserIdsText = setCommsUserIdsText || (() => {});
  const setBatchLabel = setCommsBatchLabel || (() => {});
  const setSubject = setCommsSubject || (() => {});
  const setBody = setCommsBody || (() => {});
  const setRQuery = setRecipientQuery || (() => {});
  const addRecipient = onAddRecipient || (() => {});
  const removeRecipient = onRemoveRecipient || (() => {});
  const openAudience = onOpenAudiencePicker || (() => {});
  const clearAudience = onClearAudience || (() => {});
  const dismissLastResult = setCommsLastResult || (() => {});

  const recipientsCount = Array.isArray(selectedRecipients) ? selectedRecipients.length : 0;

  const canSend =
    !commsLoading &&
    String(commsBody || "").trim().length > 0 &&
    (isSms || String(commsSubject || "").trim().length > 0) &&
    // mode-specific recipient readiness (soft checks; server still validates)
    (commsMode === "filter" ||
      commsMode === "users" ||
      commsMode === "recipients" ||
      commsMode === "audience");

  function buildPayload() {
    const base = {
      channel,
      batchLabel: String(commsBatchLabel || "").trim() || undefined,
      subject: isSms ? undefined : String(commsSubject || "").trim() || undefined,
      body: String(commsBody || "").trim(),
      mode: commsMode,
    };

    if (commsMode === "filter") {
      return { ...base, scope: scopeParams };
    }

    if (commsMode === "users") {
      const ids = parseIds(commsUserIdsText);
      return { ...base, userIds: ids };
    }

    if (commsMode === "recipients") {
      const ids = (selectedRecipients || [])
        .map((p) => String(p?.id || p?.user_id || p?.uid || ""))
        .filter(Boolean);
      return { ...base, userIds: ids, recipientsCount: ids.length };
    }

    if (commsMode === "audience") {
      // we pass selection through; wiring in AdminDashboard decides final payload builder
      return { ...base, audience: audienceSelection || null, scope: scopeParams };
    }

    return base;
  }

  function scopeParamsLabel() {
    try {
      return JSON.stringify(scopeParams).replaceAll('"', "");
    } catch {
      return String(scopeParams || "");
    }
  }

  async function handleSend() {
    if (typeof onSendComms !== "function") return;
    const payload = buildPayload();
    return onSendComms(channel, payload);
  }

  return (
    <div className="space-y-6 min-w-0">
      {/* Meta */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
        <div className="flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-500/10 dark:to-amber-500/10 rounded-xl shrink-0">
              {isSms ? (
                <MessageSquare className="text-orange-600 dark:text-orange-300" size={22} />
              ) : (
                <Mail className="text-orange-600 dark:text-orange-300" size={22} />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">{isSms ? "SMS" : "Email"} Broadcast</h2>
              <p className="text-xs text-slate-500 truncate">
                Scope and permissions are enforced server-side (union / conference / district / church).
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={loadCommsMeta}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw size={16} className={commsLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {commsError ? (
          <div className="mt-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
            {commsError}
          </div>
        ) : null}

        <div className="mt-4 grid md:grid-cols-3 gap-4 min-w-0">
          <MiniStatusCard title="Channel" value={isSms ? "SMS" : "Email"} subtitle="Broadcast channel" icon={isSms ? MessageSquare : Mail} />
          <MiniStatusCard title="Provider" value={providerOk ? "Ready" : "Not ready"} subtitle="Provider health" icon={CheckCircle2} />
          <MiniStatusCard title="Scope" value={(scopeChips || []).join(" • ") || "Union (all)"} subtitle="Current selection" icon={Building2} />
        </div>
      </div>

      {/* Send */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between min-w-0">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold">Send</h3>
            <p className="text-xs text-slate-500 mt-1">
              Choose a mode: scope filter, explicit IDs, individual recipients, or Audience groups.
            </p>
          </div>

          <div className="text-xs text-slate-500 shrink-0">
            Endpoint: <span className="font-mono">/api/admin/comms/send</span>
          </div>
        </div>

        {/* Mode pills */}
        <div className="mt-4 flex flex-wrap gap-2">
          <ModePill
            active={commsMode === "filter"}
            onClick={() => setMode("filter")}
            icon={Building2}
            label="Filter (scope)"
          />
          <ModePill
            active={commsMode === "users"}
            onClick={() => setMode("users")}
            icon={Users}
            label="User IDs"
          />
          <ModePill
            active={commsMode === "recipients"}
            onClick={() => setMode("recipients")}
            icon={Search}
            label={`Individuals${recipientsCount ? ` (${recipientsCount})` : ""}`}
          />
          <ModePill
            active={commsMode === "audience"}
            onClick={() => setMode("audience")}
            icon={Layers}
            label="Audience"
          />
        </div>

        {/* Top fields */}
        <div className="mt-4 grid md:grid-cols-3 gap-4 min-w-0">
          <div className="min-w-0">
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              Batch label (optional)
            </label>
            <input
              value={commsBatchLabel}
              onChange={(e) => setBatchLabel(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
              placeholder="e.g. Jan outreach"
            />
          </div>

          {!isSms ? (
            <div className="md:col-span-2 min-w-0">
              <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Subject
              </label>
              <input
                value={commsSubject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
                placeholder="Email subject…"
              />
            </div>
          ) : (
            <div className="hidden md:block" />
          )}
        </div>

        {/* Mode body */}
        {commsMode === "filter" ? (
          <ModeCard
            title="Filter (scope)"
            subtitle="Sends to learners in the currently selected scope (server enforces)."
            right={
              <span className="text-xs text-slate-500">
                Scope params: <span className="font-mono">{scopeParamsLabel()}</span>
              </span>
            }
          />
        ) : null}

        {commsMode === "users" ? (
          <ModeCard
            title="User IDs"
            subtitle="Paste comma/space separated user IDs. Non-union admins are still fenced by scope."
          >
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
              User IDs (comma / space separated)
            </label>
            <textarea
              rows={2}
              value={commsUserIdsText}
              onChange={(e) => setUserIdsText(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
              placeholder="uuid1, uuid2, uuid3"
            />
            <div className="mt-2 text-xs text-slate-500">
              Parsed IDs: <span className="font-semibold">{parseIds(commsUserIdsText).length}</span>
            </div>
          </ModeCard>
        ) : null}

        {commsMode === "recipients" ? (
          <ModeCard
            title="Individuals"
            subtitle="Search people (wired in AdminDashboard) then add them to recipients."
            right={
              <span className="text-xs text-slate-500">
                Selected:{" "}
                <span className="font-semibold">{recipientsCount}</span>
                {maxRecipients ? <> / {maxRecipients}</> : null}
              </span>
            }
          >
            <div className="grid md:grid-cols-2 gap-4 min-w-0">
              {/* Search */}
              <div className="min-w-0">
                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
                  Search individuals
                </label>
                <div className="relative min-w-0">
                  <input
                    value={recipientQuery}
                    onChange={(e) => setRQuery(e.target.value)}
                    placeholder="Search name/email/phone…"
                    className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
                  />
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>

                <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
                  <div className="px-3 py-2 bg-slate-50 dark:bg-slate-950 text-xs text-slate-500 flex items-center justify-between">
                    <span>Results</span>
                    <span className="font-semibold">{Array.isArray(recipientResults) ? recipientResults.length : 0}</span>
                  </div>

                  <div className="max-h-64 overflow-auto">
                    {(recipientResults || []).slice(0, 50).map((p) => {
                      const id = String(p?.id || p?.user_id || p?.uid || "");
                      const name = p?.full_name || p?.fullName || p?.name || p?.email || "User";
                      const secondary = p?.email || p?.phone || "";
                      const already =
                        (selectedRecipients || []).some((x) => String(x?.id || x?.user_id || x?.uid || "") === id);

                      return (
                        <button
                          key={id || `${name}-${secondary}`}
                          type="button"
                          onClick={() => {
                            if (!id || already) return;
                            if (maxRecipients && recipientsCount >= maxRecipients) return;
                            addRecipient(p);
                          }}
                          className={`w-full text-left px-3 py-2 border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                            already ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold truncate">{name}</div>
                              <div className="text-xs text-slate-500 truncate">{secondary}</div>
                            </div>
                            <span
                              className={`text-[11px] px-2 py-1 rounded-full border shrink-0 ${
                                already
                                  ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700"
                                  : "bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200 border-orange-200 dark:border-orange-800"
                              }`}
                            >
                              {already ? "Added" : "Add"}
                            </span>
                          </div>
                        </button>
                      );
                    })}

                    {(recipientResults || []).length === 0 ? (
                      <div className="px-3 py-6 text-sm text-slate-500">
                        No results yet. Type to search (wired in AdminDashboard).
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 text-xs text-slate-500">
                  Tip: keep searches short (e.g. “john”, “@gmail”, “070…”).
                </div>
              </div>

              {/* Selected */}
              <div className="min-w-0">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">Selected recipients</div>
                  {recipientsCount > 0 ? (
                    <button
                      type="button"
                      onClick={() => {
                        // remove all (best effort)
                        (selectedRecipients || []).forEach((p) => removeRecipient(p));
                      }}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 text-xs font-semibold"
                    >
                      <Trash2 size={14} />
                      Clear
                    </button>
                  ) : null}
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 dark:bg-slate-950 text-xs text-slate-500 flex items-center justify-between">
                    <span>Recipients</span>
                    <span className="font-semibold">{recipientsCount}</span>
                  </div>

                  <div className="max-h-64 overflow-auto">
                    {(selectedRecipients || []).map((p) => {
                      const id = String(p?.id || p?.user_id || p?.uid || "");
                      const name = p?.full_name || p?.fullName || p?.name || p?.email || "User";
                      const secondary = p?.email || p?.phone || "";

                      return (
                        <div
                          key={id || `${name}-${secondary}`}
                          className="px-3 py-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{name}</div>
                            <div className="text-xs text-slate-500 truncate">{secondary}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRecipient(p)}
                            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                            title="Remove"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      );
                    })}

                    {recipientsCount === 0 ? (
                      <div className="px-3 py-6 text-sm text-slate-500">No recipients selected yet.</div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 text-xs text-slate-500">
                  These recipients are sent as <span className="font-mono">userIds[]</span> in the payload.
                </div>
              </div>
            </div>
          </ModeCard>
        ) : null}

        {commsMode === "audience" ? (
          <ModeCard
            title="Audience"
            subtitle="Send to selected groups from AudiencePicker (we’ll wire the payload builder in AdminDashboard next)."
            right={
              <span className="text-xs text-slate-500">
                Scope params: <span className="font-mono">{scopeParamsLabel()}</span>
              </span>
            }
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <button
                type="button"
                onClick={openAudience}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
              >
                <Layers size={16} />
                Choose audience
              </button>

              <button
                type="button"
                onClick={clearAudience}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Clear
              </button>

              <div className="text-xs text-slate-500 sm:ml-auto">
                {audienceSelection ? (
                  <span className="font-semibold">Audience selected</span>
                ) : (
                  "No audience selected yet."
                )}
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-3 text-xs text-slate-600 dark:text-slate-300">
              <div className="font-semibold text-slate-700 dark:text-slate-200 mb-1">Audience payload preview</div>
              <div className="font-mono break-words">
                {(() => {
                  try {
                    return JSON.stringify(audienceSelection || null, null, 0);
                  } catch {
                    return String(audienceSelection || "");
                  }
                })()}
              </div>
            </div>
          </ModeCard>
        ) : null}

        {/* Message body */}
        <div className="mt-4 min-w-0">
          <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2">
            {isSms ? "SMS message" : "Email message"}
          </label>
          <textarea
            rows={6}
            value={commsBody}
            onChange={(e) => setBody(e.target.value)}
            className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-950 text-sm"
            placeholder={isSms ? "Write SMS message…" : "Write email message…"}
          />
          <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
            <span>
              {isSms ? "Tip: keep SMS concise." : "Tip: use a clear subject + short paragraphs."}
            </span>
            <span className="font-mono">{String(commsBody || "").length} chars</span>
          </div>
        </div>

        {/* Send bar */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-xs text-slate-500">
            Current scope params:{" "}
            <span className="font-mono">{scopeParamsLabel()}</span>
          </div>

          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            <Send size={16} />
            {commsLoading ? "Sending…" : "Send"}
          </button>
        </div>

        {/* Last result */}
        {commsLastResult ? (
          <div className="mt-4 p-4 rounded-xl border border-green-200 bg-green-50 text-green-900 dark:bg-green-900/20 dark:border-green-800 dark:text-green-100">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold">Batch sent</div>
                <div className="text-sm mt-1 break-words">
                  Batch ID: <span className="font-mono">{commsLastResult.batchId}</span>{" "}
                  {commsLastResult.batchLabel ? (
                    <>
                      • Label: <span className="font-semibold">{commsLastResult.batchLabel}</span>
                    </>
                  ) : null}
                </div>
                <div className="text-sm mt-1">
                  Recipients: <span className="font-semibold">{commsLastResult.recipients ?? 0}</span> • Sent:{" "}
                  <span className="font-semibold">{commsLastResult.sent ?? 0}</span> • Failed:{" "}
                  <span className="font-semibold">{commsLastResult.failed ?? 0}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => dismissLastResult(null)}
                className="p-2 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30"
                title="Dismiss"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Batches + Logs */}
      <div className="grid lg:grid-cols-2 gap-6 min-w-0">
        {/* Batches */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="text-lg font-semibold truncate">Batches</div>
              <div className="text-xs text-slate-500 truncate">Jobs list</div>
            </div>
            <button
              type="button"
              onClick={() => loadCommsBatches?.(channel)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={batchesLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <input
              value={batchesQ}
              onChange={(e) => setBatchesQ?.(e.target.value)}
              placeholder="Search batches…"
              className="flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
            <button
              type="button"
              onClick={() => {
                setBatchesOffset?.(0);
                loadCommsBatches?.(channel);
              }}
              className="px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              Go
            </button>
          </div>

          <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="p-3 text-left">Batch</th>
                  <th className="p-3 text-center">Recipients</th>
                  <th className="p-3 text-center">Sent</th>
                  <th className="p-3 text-center">Failed</th>
                </tr>
              </thead>
              <tbody>
                {(batches || []).map((b) => (
                  <tr
                    key={b.batch_id || b.batchId || b.id}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="p-3">
                      <div className="font-semibold truncate">{b.batch_label || b.batchLabel || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono truncate">
                        {b.batch_id || b.batchId || b.id}
                      </div>
                    </td>
                    <td className="p-3 text-center">{b.recipients ?? b.total_recipients ?? 0}</td>
                    <td className="p-3 text-center">{b.sent ?? 0}</td>
                    <td className="p-3 text-center">{b.failed ?? 0}</td>
                  </tr>
                ))}
                {(batches || []).length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-slate-500">
                      No batches yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <button
              type="button"
              onClick={() => setBatchesOffset?.(Math.max(0, (batchesOffset || 0) - 25))}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              disabled={(batchesOffset || 0) <= 0}
            >
              Prev
            </button>
            <span>Offset: {batchesOffset || 0}</span>
            <button
              type="button"
              onClick={() => setBatchesOffset?.((batchesOffset || 0) + 25)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Next
            </button>
          </div>
        </div>

        {/* Logs */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 min-w-0">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="text-lg font-semibold truncate">Logs</div>
              <div className="text-xs text-slate-500 truncate">Delivery logs</div>
            </div>
            <button
              type="button"
              onClick={() => loadCommsLogs?.(channel)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <RefreshCw size={16} className={logsLoading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <input
              value={logsQ}
              onChange={(e) => setLogsQ?.(e.target.value)}
              placeholder="Search logs…"
              className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
            <input
              value={logsBatchId}
              onChange={(e) => setLogsBatchId?.(e.target.value)}
              placeholder="Batch ID…"
              className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            />
            <select
              value={logsStatus}
              onChange={(e) => setLogsStatus?.(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
            >
              <option value="">All statuses</option>
              <option value="queued">queued</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </select>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => {
                setLogsOffset?.(0);
                loadCommsLogs?.(channel);
              }}
              className="px-3 py-2 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 text-sm font-semibold"
            >
              Go
            </button>
            <span className="text-xs text-slate-500">Offset: {logsOffset || 0}</span>
          </div>

          <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="p-3 text-left">Recipient</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-left">Batch</th>
                </tr>
              </thead>
              <tbody>
                {(logs || []).map((r) => {
                  const st = String(r.status || "").toLowerCase();
                  const ok = st === "sent";

                  return (
                    <tr
                      key={r.id || `${r.batch_id}-${r.user_id}-${r.created_at}`}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="p-3">
                        <div className="font-semibold truncate">
                          {r.full_name || r.recipient_name || r.email || r.phone || "—"}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {r.email || r.phone || r.recipient || ""}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Badge
                          tone={ok ? "success" : "neutral"}
                          icon={ok ? CheckCircle2 : null}
                          label={String(r.status || "—")}
                        />
                      </td>
                      <td className="p-3">
                        <div className="text-xs font-mono truncate">{r.batch_id || r.batchId || "—"}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {(logs || []).length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-slate-500">
                      No logs yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
            <button
              type="button"
              onClick={() => setLogsOffset?.(Math.max(0, (logsOffset || 0) - 50))}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              disabled={(logsOffset || 0) <= 0}
            >
              Prev
            </button>
            <span>Offset: {logsOffset || 0}</span>
            <button
              type="button"
              onClick={() => setLogsOffset?.((logsOffset || 0) + 50)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Small local UI                                                             */
/* -------------------------------------------------------------------------- */

function MiniStatusCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 shrink-0">
          <Icon size={18} className="text-slate-700 dark:text-slate-200" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 truncate">{title}</div>
          <div className="text-base font-semibold truncate">{String(value)}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function ModePill({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-colors ${
        active
          ? "bg-orange-600 text-white border-orange-600"
          : "bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900"
      }`}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}

function ModeCard({ title, subtitle, right, children }) {
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-slate-500">{subtitle}</div>
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {children ? <div className="min-w-0">{children}</div> : null}
    </div>
  );
}

function parseIds(text) {
  return String(text || "")
    .split(/[\s,]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
}
